import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { fileUrl } from '../lib/api';
import StatCard from '../components/shared/StatCard';
import StatusBadge from '../components/shared/StatusBadge';
import ExportPanel from '../components/ExportPanel';
import { FolderOpen, ShoppingCart, IndianRupee, Users, Bot, ArrowRight, Image as ImageIcon, Flame } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const STATUS_COLORS = { DELIVERED: '#00C896', SHIPPED: '#3B82F6', CONFIRMED: '#6C5CE7', PROCESSING: '#F59E0B', PENDING_PAYMENT: '#9191A8', AWAITING_VERIFICATION: '#FF6B35', PACKED: '#A29BFE', CANCELLED: '#EF4444' };

// Convert the backend's per-metric trend object into StatCard's `trend` prop.
// The backend returns { pct: number|null, positive: bool, baseline_zero: bool }.
const toTrend = (t) => {
  if (!t) return undefined;
  if (t.baseline_zero) return { value: 'New', positive: true, baselineZero: true, hint: 'vs last 30 days' };
  const abs = Math.abs(t.pct ?? 0).toFixed(1);
  return { value: `${abs}%`, positive: !!t.positive, hint: 'vs last 30 days' };
};

// Human-friendly relative label used on the Recent Campaigns widget rows.
// Matches the approved design: "Today", "Yesterday", "3 Days Ago", …
const relDay = (iso) => {
  if (!iso) return '';
  const created = new Date(iso);
  const today = new Date();
  const d1 = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const d2 = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  const diff = Math.round((d1 - d2) / (24 * 60 * 60 * 1000));
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return `${diff} Days Ago`;
};

// Human labels for the donut legend and status counts panel (Feb 2026
// approved dashboard). Keys match the backend `data.status_counts` keys.
const STATUS_LABELS = {
  PENDING_PAYMENT: 'Pending Payment',
  AWAITING_VERIFICATION: 'Awaiting Verification',
  CONFIRMED: 'Confirmed',
  PROCESSING: 'Processing',
  PACKED: 'Packed',
  SHIPPED: 'Shipped',
  DELIVERED: 'Delivered',
  CANCELLED: 'Cancelled',
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    api.get('/analytics/overview').then(({ data }) => setData(data));
    api.get('/orders?limit=5').then(({ data }) => setOrders(data.items || []));
    api.get('/analytics/recent-campaigns?limit=5').then(({ data }) => setCampaigns(data.items || []));
  }, []);

  if (!data) return <div className="text-fab-text-muted">Loading dashboard...</div>;

  const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
  const donut = Object.entries(data.status_counts || {}).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k, value: v }));
  const totalOrders = Number(data.total_orders || 0);

  return (
    <div className="space-y-6 fade-up" data-testid="dashboard-page">
      {/* KPIs + Buyer & Order CSV Report — 5-cell row per Feb 2026 approved design */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          icon={Users}
          label="Active Buyers"
          value={Number(data.active_buyers || 0).toLocaleString('en-IN')}
          trend={toTrend(data.trend?.active_buyers)}
          accent="blue"
          testId="kpi-active-buyers"
        />
        <StatCard
          icon={FolderOpen}
          label="Total Collections"
          value={Number(data.total_collections || 0).toLocaleString('en-IN')}
          trend={toTrend(data.trend?.collections)}
          accent="primary"
          testId="kpi-collections"
        />
        <StatCard
          icon={ShoppingCart}
          label="Total Orders"
          value={Number(data.total_orders || 0).toLocaleString('en-IN')}
          trend={toTrend(data.trend?.orders)}
          accent="orange"
          testId="kpi-orders"
        />
        <StatCard
          icon={IndianRupee}
          label="Revenue"
          value={fmt(data.revenue)}
          trend={toTrend(data.trend?.revenue)}
          accent="success"
          testId="kpi-revenue"
        />
        <ExportPanel compact />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="fab-card p-4 sm:p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-display font-bold text-fab-text">Orders Overview</div>
              <div className="text-[12px] text-fab-text-muted">Last 30 days</div>
            </div>
            <Link to="/dashboard/analytics" className="text-[12px] text-fab-primary font-semibold flex items-center gap-1 hover:underline" data-testid="orders-overview-view-report">
              View Report <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="h-[200px] sm:h-[240px]">
            <ResponsiveContainer>
              <AreaChart data={data.timeline}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6C5CE7" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#6C5CE7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#9191A8' }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#9191A8' }} width={28} />
                <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #EAE8FF', fontSize: 12 }} />
                <Area type="monotone" dataKey="orders" stroke="#6C5CE7" strokeWidth={2.5} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="fab-card p-4 sm:p-5">
          <div className="font-display font-bold text-fab-text mb-0.5">Order Status</div>
          <div className="text-[12px] text-fab-text-muted mb-2" data-testid="order-status-total">{totalOrders} Total</div>
          <div className="flex items-center gap-3">
            <div className="w-[140px] h-[140px] shrink-0">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={donut} dataKey="value" innerRadius={38} outerRadius={62} paddingAngle={2}>
                    {donut.map((e, i) => <Cell key={i} fill={STATUS_COLORS[e.name] || '#A29BFE'} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 10, border: '1px solid #EAE8FF', fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 min-w-0 space-y-1" data-testid="order-status-legend">
              {donut.length === 0 ? (
                <div className="text-[12px] text-fab-text-muted">No orders yet</div>
              ) : donut.map((e) => {
                const pct = totalOrders > 0 ? Math.round((e.value / totalOrders) * 100) : 0;
                return (
                  <div key={e.name} className="flex items-start gap-2 text-[12px]">
                    <span className="w-2 h-2 mt-1.5 rounded-full shrink-0" style={{ background: STATUS_COLORS[e.name] || '#A29BFE' }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-fab-text font-semibold truncate">{STATUS_LABELS[e.name] || e.name}</div>
                      <div className="text-fab-text-muted">{e.value} ({pct}%)</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="fab-card p-4 sm:p-5 bg-gradient-to-br from-fab-primary-lighter to-white flex flex-col">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center"><Bot className="w-5 h-5 text-fab-primary" /></div>
            <div>
              <div className="font-display font-bold text-fab-text text-[14px]">AI Assistant</div>
              <div className="text-[11px] text-fab-wa-dark font-semibold flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-fab-wa live-dot" /> Active</div>
            </div>
          </div>
          <p className="text-[12px] text-fab-text-secondary leading-relaxed">AI is listening and ready to assist your buyers 24×7.</p>
          <div className="mt-4 grid grid-cols-2 gap-2 text-center">
            <div className="rounded-lg bg-white p-2">
              <div className="text-[10px] text-fab-text-muted">Conversations</div>
              <div className="font-display font-bold text-fab-text" data-testid="ai-conversations">{data.conversations}</div>
            </div>
            <div className="rounded-lg bg-white p-2">
              <div className="text-[10px] text-fab-text-muted inline-flex items-center gap-1"><Flame className="w-3 h-3 text-orange-500" /> Hot Leads</div>
              <div className="font-display font-bold text-fab-orange" data-testid="ai-hot-leads">{data.hot_leads}</div>
            </div>
          </div>
          <Link
            to="/dashboard/ai"
            className="mt-4 inline-flex items-center justify-center px-3 py-2 rounded-lg border border-fab-primary text-fab-primary text-[13px] font-semibold hover:bg-fab-primary hover:text-white transition-colors"
            data-testid="ai-open-assistant"
          >Open Assistant</Link>
        </div>
      </div>

      {/* Recent orders + WA */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="fab-card p-4 sm:p-5 lg:col-span-3">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-fab-text">Recent Orders</div>
            <Link to="/dashboard/orders" className="text-[12px] text-fab-primary font-semibold flex items-center gap-1">View All <ArrowRight className="w-3 h-3" /></Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="recent-orders-table">
              <thead>
                <tr className="text-[10px] text-fab-text-muted uppercase tracking-wider bg-fab-elevated">
                  <th className="text-left px-3 py-2 font-semibold">Order ID</th>
                  <th className="text-left px-3 py-2 font-semibold">Customer</th>
                  <th className="text-left px-3 py-2 font-semibold">Items</th>
                  <th className="text-left px-3 py-2 font-semibold">Amount</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id} className="border-b border-fab-primary-lighter last:border-0 hover:bg-fab-elevated cursor-pointer">
                    <td className="px-3 py-2.5"><Link to={`/dashboard/orders/${o.id}`} className="text-fab-primary font-semibold font-mono">{o.order_number}</Link></td>
                    <td className="px-3 py-2.5 text-fab-text">{o.buyer_name}</td>
                    <td className="px-3 py-2.5 text-fab-text-secondary">{o.items?.length} items</td>
                    <td className="px-3 py-2.5 text-fab-text font-semibold">{fmt(o.total_amount)}</td>
                    <td className="px-3 py-2.5"><StatusBadge status={o.status} size="sm" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="fab-card p-4 sm:p-5 lg:col-span-2" data-testid="recent-campaigns-widget">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-fab-text">Recent Campaigns</div>
            <Link
              to="/dashboard/broadcast"
              data-testid="recent-campaigns-view-all"
              className="text-[12px] text-fab-primary font-semibold flex items-center gap-1 hover:underline"
            >
              View All <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-fab-primary-lighter">
            {campaigns.length === 0 && (
              <div className="py-6 text-center text-fab-text-muted text-[13px]" data-testid="recent-campaigns-empty">
                No campaigns yet. Broadcast your first collection to see it here.
              </div>
            )}
            {campaigns.map((c) => (
              <div
                key={c.id}
                className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
                data-testid={`campaign-row-${c.id}`}
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-fab-elevated shrink-0 flex items-center justify-center">
                  {c.thumb_path ? (
                    <img
                      src={fileUrl(c.thumb_path)}
                      alt={c.name}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <ImageIcon className="w-4 h-4 text-fab-text-muted" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-fab-text truncate">{c.name}</div>
                  <div className="text-[11px] text-fab-text-muted">{relDay(c.created_at)}</div>
                </div>
                <div className={`text-[12px] font-semibold flex items-center gap-1.5 shrink-0 ${c.status === 'FAILED' ? 'text-red-600' : 'text-emerald-600'}`}>
                  <span className={`w-2 h-2 rounded-full ${c.status === 'FAILED' ? 'bg-red-500' : 'bg-emerald-500'}`} />
                  {c.status_label || 'Active'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
